import type { StyleResolverContext, CssFragment } from './index';

// ============================================================================
// Types
// ============================================================================

export interface DisplayFlexProps {
  hidden?: boolean;
  block?: boolean;
  flexRow?: boolean;
  flexColumn?: boolean;
  inlineBlock?: boolean;
  inlineFlex?: boolean;
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
}

// ============================================================================
// Resolution helpers
// ============================================================================

/** Derive CSS display from binary props. Priority: hidden -> block -> flexRow/flexColumn -> inlines. */
const getDisplayFromProps = (props: {
  hidden?: boolean;
  block?: boolean;
  flexRow?: boolean;
  flexColumn?: boolean;
  inlineBlock?: boolean;
  inlineFlex?: boolean;
}): string => {
  if (props.hidden) return 'none';
  if (props.block) return 'block';
  if (props.flexRow || props.flexColumn) return 'flex';
  if (props.inlineBlock) return 'inline-block';
  if (props.inlineFlex) return 'inline-flex';
  return 'block';
};

/** Returns CSS fragments for display, align-items, justify-content. */
const resolveDisplayFlexCss = (props: DisplayFlexProps): CssFragment[] => [
  `display: ${getDisplayFromProps(props)};`,
  props.alignItems ? `align-items: ${props.alignItems};` : null,
  props.justifyContent ? `justify-content: ${props.justifyContent};` : null,
];

// ============================================================================
// Picker + style resolver
// ============================================================================

const pickDisplayFlexProps = <T extends Partial<DisplayFlexProps>>(props: T): DisplayFlexProps => {
  const { hidden, block, flexRow, flexColumn, inlineBlock, inlineFlex, alignItems, justifyContent } = props;
  return { hidden, block, flexRow, flexColumn, inlineBlock, inlineFlex, alignItems, justifyContent };
};

export const resolveDisplayStyleCss = (props: Record<string, unknown>, _theme: StyleResolverContext): CssFragment[] =>
  resolveDisplayFlexCss(pickDisplayFlexProps(props as Partial<DisplayFlexProps>));
